<template>
    <div class="network-overview">
        <div class="overview-header">
            <h3 class="overview-title">수집 인프라</h3>
        </div>

        <div class="network-tree">
            <!-- 1단계: 서버 -->
            <div v-if="getDirectServerNodes().length > 0" class="level-section">
                <div class="level-header">
                    <div class="icon-container server-icon">
                        <i class="fa fa-desktop"></i>
                    </div>
                    <div class="content-text">
                        <div class="level-title">데이터 수집서버</div>
                        <div class="level-count">({{ getDirectServerNodes().length }}개)</div>
                    </div>
                </div>
                <div class="table-container server-table-container">
                    <table class="network-table">
                        <thead>
                            <tr>
                                <th>서버명</th>
                                <th>RAM 사용량</th>
                                <th>HDD 사용량</th>
                                <th>채널 연결</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(server, index) in getDirectServerNodes()"
                                :key="`server-${server.id || index}-${server.data.serverName || 'unknown'}`"
                            >
                                <td class="name-cell">{{ server.data.serverName }}</td>
                                <td class="detail-cell">
                                    <div class="progress-container">
                                        <div class="progress-bar-container">
                                            <div
                                                class="custom-progress"
                                                :style="{
                                                    '--progress-color':
                                                        getRamPercentage(server) > 80 ? '#dc3545' : '#28a745',
                                                    height: '25px',
                                                }"
                                            >
                                                <div
                                                    class="progress-bar"
                                                    :style="{ width: getRamPercentage(server) + '%' }"
                                                >
                                                    {{ formatRamUsage(server) }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="detail-cell">
                                    <div class="progress-container">
                                        <div class="progress-bar-container">
                                            <div
                                                class="custom-progress"
                                                :style="{
                                                    '--progress-color':
                                                        getHddPercentage(server) > 80 ? '#dc3545' : '#007bff',
                                                    height: '25px',
                                                }"
                                            >
                                                <div
                                                    class="progress-bar"
                                                    :style="{ width: getHddPercentage(server) + '%' }"
                                                >
                                                    {{ formatHddUsage(server) }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                <td class="status-cell">
                                    <span
                                        v-if="server.children && server.children.length > 0"
                                        class="status-badge active"
                                    >
                                        {{ server.children.length }}개
                                    </span>
                                    <span v-else class="status-badge inactive">연결 없음</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- 2단계: 채널 -->
            <div v-if="hasServerChildren" class="level-section">
                <div class="level-header">
                    <div class="icon-container channel-icon">
                        <i class="fa fa-cog"></i>
                    </div>
                    <div class="content-text">
                        <div class="level-title">채널</div>
                        <div class="level-count">({{ getTotalChannelCount() }}개)</div>
                    </div>
                </div>
                <div class="table-container channel-table-container">
                    <table class="network-table">
                        <thead>
                            <tr>
                                <th>서버</th>
                                <th>채널명</th>
                                <th>채널타입</th>
                                <th>호스트/포트</th>
                                <th>스캔/타임아웃</th>
                                <th>계측장치 연결</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="server in getServerNodes()">
                                <tr :key="`server-${server.id || 'root'}-${server.data.serverName || 'unknown'}`">
                                    <td class="server-cell" :rowspan="server.children.length">
                                        <div class="server-info">
                                            <span class="server-name">{{ server.data.serverName }}</span>
                                            <span class="server-count">({{ server.children.length }}개)</span>
                                        </div>
                                    </td>
                                    <td class="name-cell">{{ server.children[0].data.channelName }}</td>
                                    <td class="type-cell">{{ server.children[0].data.channelType }}</td>
                                    <td class="detail-cell">
                                        {{ getChannelHostPort(server.children[0]) }}
                                    </td>
                                    <td class="detail-cell">
                                        {{
                                            formatScanRate(
                                                server.children[0].data.scan ||
                                                    server.children[0].data.channelOption?.scan
                                            )
                                        }}
                                        /
                                        {{
                                            server.children[0].data.timeout ||
                                            server.children[0].data.channelOption?.timeout
                                        }}ms
                                    </td>
                                    <td class="status-cell">
                                        <span
                                            v-if="server.children[0].children && server.children[0].children.length > 0"
                                            class="status-badge active"
                                        >
                                            {{ server.children[0].children.length }}개
                                        </span>
                                        <span v-else class="status-badge inactive">없음</span>
                                    </td>
                                </tr>
                                <tr
                                    v-for="(channel, index) in server.children.slice(1)"
                                    :key="`channel-${channel.id || index}-${channel.data.channelName || 'unknown'}`"
                                >
                                    <td class="name-cell">{{ channel.data.channelName }}</td>
                                    <td class="type-cell">{{ channel.data.channelType }}</td>
                                    <td class="detail-cell">
                                        {{ getChannelHostPort(channel) }}
                                    </td>
                                    <td class="detail-cell">
                                        {{ formatScanRate(channel.data.scan || channel.data.channelOption?.scan) }} /
                                        {{ channel.data.timeout || channel.data.channelOption?.timeout }}ms
                                    </td>
                                    <td class="status-cell">
                                        <span
                                            v-if="channel.children && channel.children.length > 0"
                                            class="status-badge active"
                                        >
                                            {{ channel.children.length }}개
                                        </span>
                                        <span v-else class="status-badge inactive">없음</span>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- 1단계: 설비 -->
            <div v-if="getDirectFacilityNodes().length > 0" class="level-section">
                <div class="level-header">
                    <div class="icon-container facility-icon">
                        <i class="fa fa-hdd"></i>
                    </div>
                    <div class="content-text">
                        <div class="level-title">계측장치</div>
                        <div class="level-count">({{ getDirectFacilityNodes().length }}개)</div>
                    </div>
                </div>
                <div class="table-container facility-table-container">
                    <table class="network-table">
                        <thead>
                            <tr>
                                <th>채널명</th>
                                <th>계측장치명</th>
                                <th>장치 고유번호</th>
                                <th>활용목적</th>
                                <th>설치일</th>
                                <th>적용구역</th>
                                <th>부하 연결</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(facilities, channelName) in getFacilityNodesGroupedByChannel()">
                                <tr :key="`channel-${channelName}-${facilities[0].id || 'unknown'}`">
                                    <td class="channel-cell" :rowspan="facilities.length">
                                        <div class="channel-info">
                                            <span class="channel-name">{{ channelName }}</span>
                                            <span class="facility-count">({{ facilities.length }}개)</span>
                                        </div>
                                    </td>
                                    <td class="name-cell">{{ facilities[0].data.facilityName }}</td>
                                    <td class="detail-cell">{{ facilities[0].data.deviceId || "-" }}</td>
                                    <td class="detail-cell">
                                        {{ getPurpsUseCode(facilities[0].data.purpsUseCode) || "-" }}
                                    </td>
                                    <td class="detail-cell">{{ formatDate(facilities[0].data.instDate) }}</td>
                                    <td class="detail-cell">
                                        {{ getApplyZoneIdx(facilities[0].data.applyZoneIdx) || "-" }}
                                    </td>
                                    <td class="status-cell">
                                        <span
                                            v-if="facilities[0].children && facilities[0].children.length > 0"
                                            class="status-badge active"
                                        >
                                            {{ facilities[0].children.length }}개
                                        </span>
                                        <span v-else class="status-badge inactive">연결 없음</span>
                                    </td>
                                </tr>
                                <tr
                                    v-for="(facility, index) in facilities.slice(1)"
                                    :key="`facility-${facility.id || index}-${facility.data.facilityName || 'unknown'}`"
                                >
                                    <td class="name-cell">{{ facility.data.facilityName }}</td>
                                    <td class="detail-cell">{{ facility.data.deviceId || "-" }}</td>
                                    <td class="detail-cell">
                                        {{ getPurpsUseCode(facility.data.purpsUseCode) || "-" }}
                                    </td>
                                    <td class="detail-cell">{{ formatDate(facility.data.instDate) }}</td>
                                    <td class="detail-cell">
                                        <!-- {{ facility.data }} -->
                                        {{ getApplyZoneIdx(facility.data.applyZoneIdx) || "-" }}
                                    </td>
                                    <td class="status-cell">
                                        <span
                                            v-if="facility.children && facility.children.length > 0"
                                            class="status-badge active"
                                        >
                                            {{ facility.children.length }}개
                                        </span>
                                        <span v-else class="status-badge inactive">연결 없음</span>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
export default {
    name: "NetworkOverview",
    props: {
        parentData: {
            type: Object,
            required: false,
        },
    },
    computed: {
        ...mapState({
            commonCodes: (state) => state.commonCodes,

            zoneInfo: (state) => state.zoneInfo,
        }),

        hasServerChildren() {
            if (!this.parentData || !this.parentData.children) return false;
            return this.parentData.children?.some(
                (child) => child.data.type === "server" && child.children && child.children.length > 0
            );
        },
    },
    mounted() {},
    methods: {
        getPurpsUseCode(code) {
            return this.commonCodes.purpsUseCode.find((item) => item.value === code)?.text;
        },
        getApplyZoneIdx(code) {
            const result = this.zoneInfo.find((item) => item.value === code);
            return result?.text;
        },

        formatScanRate(scan) {
            if (!scan) return "-";
            const scanMs = parseInt(scan);
            if (scanMs < 1000) return `${scanMs}ms`;
            if (scanMs < 60000) return `${scanMs / 1000}s`;
            return `${scanMs / 60000}분`;
        },

        formatDate(dateStr) {
            if (!dateStr) return "-";
            try {
                const date = new Date(dateStr);
                return date.toLocaleDateString("ko-KR");
            } catch {
                return dateStr;
            }
        },

        formatRamUsage(server) {
            if (!server.data.serverStatus?.ram) return "-";
            const ram = server.data.serverStatus.ram;
            const usedPercent = ((ram.ramUsed / ram.ramTotal) * 100).toFixed(1);
            return `${ram.ramUsed.toFixed(1)}GB / ${ram.ramTotal.toFixed(1)}GB (${usedPercent}%)`;
        },

        formatHddUsage(server) {
            if (!server.data.serverStatus?.hdd) return "-";
            const hdd = server.data.serverStatus.hdd;
            const usedPercent = ((hdd.hddUsed / hdd.hddTotal) * 100).toFixed(1);
            return `${hdd.hddUsed.toFixed(1)}GB / ${hdd.hddTotal.toFixed(1)}GB (${usedPercent}%)`;
        },

        getChannelHostPort(channel) {
            if (channel.data.channelOption?.setting?.server) {
                const server = channel.data.channelOption.setting.server;
                return `${server.host || "-"}:${server.port || "-"}`;
            }
            return "-";
        },

        getServerNodes() {
            return (
                this.parentData.children?.filter(
                    (child) => child.data.type === "server" && child.children && child.children.length > 0
                ) || []
            );
        },

        getDirectServerNodes() {
            return this.getAllNodesByType("server");
        },

        getDirectFacilityNodes() {
            const facilities = this.getAllFacilityNodesWithParent();
            return facilities;
        },

        // 재귀적으로 모든 노드를 순회하여 특정 타입의 노드들을 찾는 메서드
        getAllNodesByType(type) {
            const nodes = [];
            this.traverseNodes(this.parentData, type, nodes);
            return nodes;
        },

        // 재귀적으로 노드를 순회하는 헬퍼 메서드
        traverseNodes(node, targetType, resultArray) {
            if (!node || !node.children) return;

            node.children.forEach((child) => {
                if (child.data && child.data.type === targetType) {
                    resultArray.push(child);
                }
                // 재귀적으로 자식 노드들도 탐색
                this.traverseNodes(child, targetType, resultArray);
            });
        },

        // 설비 노드와 부모 채널 정보를 함께 수집하는 메서드
        getAllFacilityNodesWithParent() {
            const facilities = [];
            this.traverseFacilityNodesWithParent(this.parentData, facilities);
            return facilities;
        },

        // 설비 노드와 부모 채널 정보를 재귀적으로 탐색하는 헬퍼 메서드
        traverseFacilityNodesWithParent(node, resultArray, parentChannel = null) {
            if (!node || !node.children) return;

            node.children.forEach((child) => {
                if (child.data && child.data.type === "facility") {
                    // 설비 노드에 부모 채널 정보 추가
                    const facilityWithParent = {
                        ...child,
                        parentChannel: parentChannel,
                    };
                    resultArray.push(facilityWithParent);
                } else if (child.data && child.data.type === "channel") {
                    // 채널 노드를 만나면 부모 채널 정보로 설정
                    const channelInfo = {
                        channelName: child.data.channelName,
                        channelType: child.data.channelType,
                    };
                    // 자식 노드들을 탐색할 때 이 채널을 부모로 전달
                    this.traverseFacilityNodesWithParent(child, resultArray, channelInfo);
                } else {
                    // 다른 타입의 노드도 재귀적으로 탐색
                    this.traverseFacilityNodesWithParent(child, resultArray, parentChannel);
                }
            });
        },

        getTotalChannelCount() {
            let count = 0;
            this.parentData.children?.forEach((child) => {
                if (child.data.type === "server" && child.children) {
                    count += child.children.length;
                }
            });
            return count;
        },

        // 설비 노드의 부모 채널명을 가져오는 메서드
        getParentChannelName(facility) {
            if (facility.parentChannel && facility.parentChannel.channelName) {
                return facility.parentChannel.channelName;
            }
            return "-";
        },

        // 설비 노드들을 채널별로 그룹화하는 메서드
        getFacilityNodesGroupedByChannel() {
            const facilities = this.getAllFacilityNodesWithParent();
            const grouped = {};

            facilities.forEach((facility) => {
                const channelName = this.getParentChannelName(facility);
                if (!grouped[channelName]) {
                    grouped[channelName] = [];
                }
                grouped[channelName].push(facility);
            });

            return grouped;
        },

        // RAM 사용률 퍼센트 계산
        getRamPercentage(server) {
            if (!server.data.serverStatus?.ram) return 0;
            const ram = server.data.serverStatus.ram;
            const percentage = (ram.ramUsed / ram.ramTotal) * 100;
            return Math.min(Math.round(percentage), 100);
        },

        // HDD 사용률 퍼센트 계산
        getHddPercentage(server) {
            if (!server.data.serverStatus?.hdd) return 0;
            const hdd = server.data.serverStatus.hdd;
            const percentage = (hdd.hddUsed / hdd.hddTotal) * 100;
            return Math.min(Math.round(percentage), 100);
        },
    },
};
</script>

<style scoped>
.network-overview {
    padding: 16px 16px 0 16px;
    height: 100%;
    overflow-y: auto;
}

.overview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e0e6ed;
}

.overview-title {
    margin: 0;
    color: black;
    font-size: 16px;
    font-weight: 600;
}

.network-tree {
    margin-top: 20px;
}

.level-section {
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    box-shadow: rgba(0, 0, 0, 0.15) 0px 3px 3px 0px;
    border-radius: 8px;
    padding: 10px;
}
.level-section:last-child {
    margin-bottom: 0;
}

.level-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 15%;
    /* padding: 16px 12px; */
    border-radius: 8px 0 0 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    /* gap: 12px; */
}

/* 노드 타입별 level-header 배경색 */
.level-section:nth-child(1) .level-header {
    background-color: #dd5a5c;
}

.level-section:nth-child(2) .level-header {
    background-color: #708d81;
}

.level-section:nth-child(3) .level-header {
    background-color: #6190c4;
}

.icon-container {
    width: 70px;
    height: 70px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
}

.icon-container i {
    color: white;
    font-size: 80%;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* 노드 타입별 아이콘 컨테이너 색상 */
.server-icon {
    /* background-color: rgba(52, 143, 226, 0.4); */
}

.channel-icon {
    /* background-color: rgba(114, 124, 182, 0.4); */
}

.facility-icon {
    /* background-color: rgba(50, 169, 50, 0.4); */
}

.server-icon,
.channel-icon,
.facility-icon {
    font-size: 400%;
}

.content-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-align: center;
}

.level-title {
    font-weight: 600;
    color: white;
    font-size: 14px;
    margin: 0;
}

.level-count {
    font-size: 12px;
    color: white;
    font-weight: 500;
    margin: 0;
}

.table-container {
    background: white;
    border-radius: 0 6px 6px 0;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    overflow-y: auto;
    width: 85%;
}

.server-table-container,
.channel-table-container,
.facility-table-container {
    min-height: 125px;
}

.server-table-container {
    max-height: 130px;
    border: 1px solid #dd5a5c;
}
.channel-table-container {
    max-height: 220px;
    border: 1px solid #708d81;
}
.facility-table-container {
    max-height: 260px;
    border: 1px solid #6190c4;
}

.network-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.network-table th {
    background: #f8f9fa;
    padding: 10px 12px;
    text-align: left;
    font-weight: 600;
    color: #495057;
    border-bottom: 1px solid #dee2e6;
    font-size: 12px;
    position: sticky;
    top: 0;
    z-index: 10;
}

.network-table td {
    padding: 8px 12px;
    border-bottom: 1px solid #f1f3f4;
    vertical-align: middle;
}

.network-table tr:hover {
    background: #f8f9fa;
}

.status-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
}

.status-badge.active {
    background: #e8f5e8;
    color: #388e3c;
}

.status-badge.inactive {
    background: #ffebee;
    color: #d32f2f;
}

.server-cell,
.channel-cell {
    background: #f8f9fa;
    font-weight: 600;
    color: #495057;
    min-width: 180px;
    max-width: 180px;
    width: 180px;
    vertical-align: top !important;
}

.server-info,
.channel-info {
    display: flex;
    gap: 4px;
    flex-direction: column;
}

.server-name,
.channel-name {
    font-weight: 600;
    color: #495057;
}
.facility-count,
.server-count,
.channel-count {
    font-size: 11px;
    color: #6c757d;
    font-weight: 500;
}

.name-cell {
    font-weight: 600;
    color: #495057;
}

.detail-cell {
    color: #6c757d;
    font-size: 12px;
}

.status-cell {
    text-align: center;
}

/* ProgressBar.vue 스타일을 참조한 프로그레스바 스타일 */

.progress-bar-container {
    display: flex;
    flex-direction: column;
    position: relative;
    flex: 1;
}

.custom-progress {
    border-radius: 20px;
    background-color: #e9ecef;
    overflow: hidden;
    position: relative;
    box-shadow:
        rgba(14, 63, 126, 0.04) 0px 0px 0px 1px,
        rgba(42, 51, 69, 0.04) 0px 1px 1px -0.5px,
        rgba(42, 51, 70, 0.04) 0px 3px 3px -1.5px,
        rgba(42, 51, 70, 0.04) 0px 6px 6px -3px,
        rgba(14, 63, 126, 0.04) 0px 12px 12px -6px,
        rgba(14, 63, 126, 0.04) 0px 24px 24px -12px;
}

.custom-progress .progress-bar {
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    transition: width 0.3s ease;
    background-color: var(--progress-color, #6c757d) !important;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding-left: 8px;
    color: white;
    height: 100%;
    box-shadow:
        rgba(0, 0, 0, 0.17) 0px -23px 25px 0px inset,
        rgba(0, 0, 0, 0.15) 0px -36px 30px 0px inset,
        rgba(0, 0, 0, 0.1) 0px -79px 40px 0px inset,
        rgba(0, 0, 0, 0.06) 0px 2px 1px,
        rgba(0, 0, 0, 0.09) 0px 4px 2px,
        rgba(0, 0, 0, 0.09) 0px 8px 4px,
        rgba(0, 0, 0, 0.09) 0px 16px 8px,
        rgba(0, 0, 0, 0.09) 0px 32px 16px;
}

.custom-progress:hover .progress-bar {
    opacity: 0.8;
}
</style>
